import React, { useState, useEffect, Suspense } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "Common/hooks/assets/theme";
import MentorContent from "PlatForms/OrgLMS/Dashboards/Mentor/MentorRouteContent/mentorContent";
import AdminContent from "PlatForms/OrgLMS/Dashboards/Admin/AdminRouteContent/adminContent";
import Login from "Common/Auth/SignIn";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import LearnerContent from "PlatForms/OrgLMS/Dashboards/Learner/LearnerRouteContent/learnerContent";
import { SignalRProvider } from "Common/hooks/context/SingleRContext";
import useSignalRPresence from "Common/SingleRConnection/useSignalRPresence";
import { setupInterceptors } from "Common/Client/Interceptor";
import PropTypes from "prop-types";
import ManagerContent from "PlatForms/OrgLMS/Dashboards/Manager/ManagerContent/ManagerContent";
import Register from "Common/Auth/SignUp";
import StartupPage from "Common/Components/startUp";
import SelfLearnerContent from "PlatForms/SelfLMS/Routes/SelfLernerRouteContent/SelfLearnerContent";
import ProAdminContent from "PlatForms/Dashboards/CORIQPROAdminDashboard/Routes/ProAdminContent";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrganizationRegistration from "Common/Auth/OrganizationRegister";
import PlatformInvite from "Common/Auth/PlatformInvite";
import PublicCertificatePage from "Common/Auth/PublicCertificatePage";
import AcceptInvite from "PlatForms/Dashboards/AcceptInvitations/AcceptInvitation";
import EmailConfirmation from "PlatForms/SelfLMS/Components/Email-Confirmation";
import RegistrationSuccess from "Common/Auth/RegistrationSuccess";
import ExpertContent from "PlatForms/Dashboards/ExpertDashboard/Routes/ExpertContent";


const Loader = ({ loading }) => {
  if (!loading) return null;
  return (
    <div className="spinner-container">
      <div className="spinner"></div>
      <div className="spinner-text">Loading...</div>
    </div>
  );
};

Loader.propTypes = {
  loading: PropTypes.bool.isRequired,
};

export default function App() {
  const [userRole, setUserRole] = useState(localStorage.getItem("role"));
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false); // Start with false
  const [interceptorsReady, setInterceptorsReady] = useState(false);

  // SETUP INTERCEPTORS FIRST - This is critical
  useEffect(() => {
    console.log(" Setting up interceptors...");
    setupInterceptors(setLoading);
    setInterceptorsReady(true);
    console.log(" Interceptors setup complete");
  }, []);

  const { unreadMessages, startPresenceHub } = useSignalRPresence();

  useEffect(() => {
    const presenceStarted = localStorage.getItem("presenceHubStarted");
    if (presenceStarted === "true") {
      startPresenceHub();
    }
  }, [startPresenceHub]);

  //  Keep role updated when storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const role = localStorage.getItem("role");
      console.log(" Role updated:", role);
      setUserRole(role);
    };
    
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  //  Check authentication and redirect on app load
  useEffect(() => {
    if (!interceptorsReady) return; // Wait for interceptors

    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const currentPath = location.pathname;

    console.log(" App.js - Auth Check:", { 
      token: token ? `Present (${token.length} chars)` : 'Missing',
      role: role || 'Not set',
      currentPath,
      interceptorsReady 
    });

    const authFlowRoutes = ["/Email-Confirmation", "/accept-invite", "/credential-verify"];
  
  if (authFlowRoutes.includes(currentPath)) {
    console.log("On Auth Flow route - bypassing all redirects");
    return; // Exit early! Do nothing.
  }

    if (token && role) {
      // User is logged in
      const publicRoutes = ["/", "/login", "/register",  "/startup"];
      
      if (publicRoutes.includes(currentPath)) {
        // Redirect to appropriate dashboard if on public route
        const dashboardPaths = {
          "Admin": "/admin-dashboard",
          "Mentor": "/mentor-dashboard", 
          "Learner": "/learner-dashboard",
          "Manager": "/manager-dashboard",
          "SelfLearner": "/selfLearner-dashboard",
          "SuperAdmin": "/proAdmin-dashboard",
          "Expert":"/expert-dashboard"
        };
        
        const targetPath = dashboardPaths[role];
        if (targetPath && targetPath !== currentPath) {
          console.log(" Redirecting to dashboard:", targetPath);
          navigate(targetPath, { replace: true });
        }
      }
    } else {
      // User is not logged in
      const protectedRoutes = [
        "/admin-dashboard", "/mentor-dashboard", "/learner-dashboard", 
        "/manager-dashboard", "/selfLearner-dashboard", "/proAdmin-dashboard","expert-dashboard"
      ];
      
      if (protectedRoutes.some(route => currentPath.startsWith(route))) {
        console.log(" No auth, redirecting to login");
        navigate("/login", { replace: true });
      }
    }
  }, [navigate, location.pathname, interceptorsReady]);

  // Show loader only when interceptors aren't ready
  if (!interceptorsReady) {
    return <Loader loading={true} />;
  }

  return (
    <SignalRProvider unreadMessages={unreadMessages}>
      <Suspense fallback={<Loader loading={loading} />}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />

          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/accept-invite" element={<AcceptInvite/>} />
            <Route path="/email-confirmation" element = {< EmailConfirmation/>} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
             <Route path="/platform-invite" element={<PlatformInvite />} />
            <Route path="/startup" element={<StartupPage />} />
            <Route path="/register/organization" element={<OrganizationRegistration />} />
    <Route path="/credential-verify" element={<PublicCertificatePage />} />

            {/* Protected routes based on role */}
            {userRole === "Admin" && <Route path="/*" element={<AdminContent />} />}
            {userRole === "Mentor" && <Route path="/*" element={<MentorContent />} />}
            {userRole === "Learner" && <Route path="/*" element={<LearnerContent />} />}
            {userRole === "Manager" && <Route path="/*" element={<ManagerContent />} />}
            {userRole === "SelfLearner" && <Route path="/*" element={<SelfLearnerContent />} />}
            {userRole === "SuperAdmin" && <Route path="/*" element={<ProAdminContent />} />}
            {userRole === "Expert" && <Route path="/*" element={<ExpertContent />} />}
            

            {/* Explicit routes for direct navigation */}
            <Route path="/selfLearner-dashboard/*" element={
              userRole === "SelfLearner" ? <SelfLearnerContent /> : <Login />
            } />
            <Route path="/proAdmin-dashboard/*" element={
              userRole === "SuperAdmin" ? <ProAdminContent /> : <Login />
            } />
              <Route path="/expert-dashboard/*" element={
              userRole === "Expert" ? <ExpertContent /> : <Login />
            } />

            {/* Fallback - redirect to login */}
            <Route path="*" element={<Login />} />
          </Routes>
        </ThemeProvider>
      </Suspense>
    </SignalRProvider>
  );
}